import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import OpenInNew from '@material-ui/icons/OpenInNew';
import RemoveRedEye from '@material-ui/icons/RemoveRedEye';
import { useEffect, useRef, useState } from 'react';
import { UseStore } from 'zustand';

import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { Divider } from '@mui/material';
import { mipsApi } from '../../components/mips/mips-api';
import { EditDialog, EditStore } from '../../components/trucode/edits';
import { AlertStore } from '../alerts';
import { Text } from '../controls';
import { Alert, Visit } from '../domain';
import { FormApi, formApi } from '../form';
import { withTooltip } from '../format';
import { truCode } from '../trucode';
import { AlertApi, alertsUpdated } from './alerts-api';
import { alertColor, alertToString, sortAlerts } from './alerts-model';
import { AlertDemographics } from './alerts-demographics';
import { WorksheetApis } from '../visit-context';

const tabWidth = 362;
const truncateStyle1: any = {
  paddingBottom: 5,
  color: '#666',
  fontWeight: 'bold',
  minWidth: 30,
  flex: 1,
  maxWidth: 250,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const truncateStyle2 = { ...truncateStyle1, ...{ minWidth: 35, maxWidth: 100, fontWeight: 'normal', fontStyle: 'italic', color: 'black' } };
type AlertChipProps = {
  alert: Alert;
  api?: UseStore<AlertApi>;
  edits?: EditStore;
  readonly?: boolean;
  onMipsAlert?: (a: Alert) => void;
};

export function AlertRow(props: AlertChipProps) {
  const { api, alert, edits, onMipsAlert, readonly } = props;
  let { alertType, canOverride, overrideReason, resolved } = alert;

  const isTruCode = alertType === 'TRUCODEEDIT';
  let label = alertToString(alert);

  const onTruCodeEdits = () => {
    truCode.setState({ alert });
    if (edits) {
      edits.setState({ alert });
    }
  };
  let hasTuples = !!alert?.details?.tuples?.length;
  const onMIPsTuples = () => {
    if (onMipsAlert) {
      onMipsAlert(alert);
    }
  };

  const [, setResolved] = useState(alert.resolved || alert.overridden || alert.acknowledged);
  const [ack, setAck] = useState(alert.acknowledged);

  const onOverride = () => {
    api.setState({ openAlert: alert });
  };

  const onAcknowledge = async () => {
    if (api) {
      await api.getState().acknowledgeAlert(alert);
      setResolved(alert.acknowledged);
    } else {
      alert.acknowledged = true;
    }
    setAck(alert.acknowledged);
  };

  const smallStyle = { width: '8px', height: '8px' };
  const name = alert.alertType;
  const iconColor = alertColor(alert);

  const complete = resolved || ack;

  return (
    <Grid container direction="row" spacing={1} justifyContent="flex-start" alignItems="flex-start" style={{ height: 32, maxWidth: 340, maxHeight: 32, flexWrap: 'nowrap' }}>
      <Grid item>{complete ? <CheckCircleIcon htmlColor={iconColor} /> : <ErrorOutlineIcon htmlColor={iconColor} />}</Grid>
      <Grid item style={truncateStyle1}>
        {withTooltip(label, label, 'bottom-start')}
      </Grid>
      <Grid item style={{ flexGrow: 0.3 }} />
      {!ack && !canOverride && (
        <Grid item>
          {withTooltip(
            'Acknowledge Alert',
            <IconButton disabled={readonly} id={name + '-acknowledge-btn'} name="acknowledge-alert" onClick={onAcknowledge} style={smallStyle}>
              <RemoveRedEye fontSize="small" htmlColor="black" />
            </IconButton>
          )}
        </Grid>
      )}
      {isTruCode && (
        <Grid item>
          {withTooltip(
            'View Tru Code Edits',
            <IconButton disabled={readonly} id={name + '-edits-btn'} onClick={onTruCodeEdits} style={smallStyle}>
              <OpenInNew fontSize="small" htmlColor="black" />
            </IconButton>
          )}
        </Grid>
      )}

      {hasTuples && (
        <Grid item>
          {withTooltip(
            'Select MIPS Tuples',
            <IconButton disabled={readonly} id={name + '-mips-btn'} onClick={onMIPsTuples} style={smallStyle}>
              <OpenInNew fontSize="small" htmlColor="black" />
            </IconButton>
          )}
        </Grid>
      )}
      {overrideReason && (
        <Grid item style={truncateStyle2}>
          {withTooltip(overrideReason, overrideReason, 'bottom-start')}
        </Grid>
      )}
      {canOverride && (
        <Grid item>
          {withTooltip(
            overrideReason ? 'Edit Reason' : 'Override Alert',
            <IconButton disabled={readonly} id={name + '-override-btn'} onClick={onOverride} style={smallStyle}>
              <CreateIcon fontSize="small" htmlColor="black" />
            </IconButton>
          )}
        </Grid>
      )}
    </Grid>
  );
}
type AlertListProps = {
  visit: Visit;
  apis: WorksheetApis;
  readonly?: boolean;
};

export function AlertsList(props: AlertListProps) {
  const { apis, readonly } = props;
  const { alerts: api, edits } = apis;

  const alerts = sortAlerts(api((s) => s.alerts));

  const onMipsAlert = (a: Alert) => {
    mipsApi.getState().setAlert(a);
  };

  const height = '54vh';

  return (
    <>
      <div style={{ marginTop: 12, marginLeft: 16, height: height, maxHeight: height, overflowY: 'scroll', overflowX: 'hidden', width: tabWidth, maxWidth: tabWidth }}>
        {alerts && alerts.length ? (
          <>
            {alerts.map((v) => (
              <>
                <AlertRow alert={v} api={api} edits={edits} onMipsAlert={onMipsAlert} readonly={readonly} />
                <Divider orientation="horizontal" style={{ width: '90%', marginTop: 0, marginBottom: 5 }} />
              </>
            ))}
          </>
        ) : (
          <span style={{ paddingLeft: 6 }}>No Alerts</span>
        )}
      </div>
    </>
  );
}

type OverrideDialogueProps = {
  api: AlertStore;
};
export function OverrideDialogue(props: OverrideDialogueProps) {
  const { api } = props;

  const openAlert = api((s) => s.openAlert);
  const form: FormApi = useRef(
    formApi({
      valid: (v) => v.reason?.length >= 5,
      onSubmit: async (v) => {
        const a = api.getState().openAlert;
        if (a) {
            a.overrideReason = v.reason;
          await api.getState().overrideAlert(a, v.reason);
          api.setState({ openAlert: null });
        }
      },
      values: {
        reason: null,
      },
      fields: {
        reason: {
          id: 'override-reason',
          required: true,
          label: 'Reason (5 Characters or More)',
          multiline: true,
          style: { width: '100%' },
          rows: 3,
          onEnter: () => form.submit(),
        },
      },
    })
  ).current;

  useEffect(() => {
    if (openAlert?.overrideReason) {
      form.setValues({ reason: openAlert.overrideReason });
    }
  });
  return (
    <Dialog open={!!openAlert} maxWidth={false} style={{ zIndex: 1531 }}>
      <DialogTitle>Override Alert</DialogTitle>
      <DialogContent style={{ minWidth: 400 }}>
        <Text {...form.controls.reason} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => api.setState({ openAlert: null })} color="primary" variant="outlined">
          Cancel
        </Button>
        <Button id="override-submit-btn" onClick={() => form.submit()} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AlertsTab(props: AlertListProps) {
  const { apis, visit } = props;
  alertsUpdated((s) => s.b);
  return (
    <>
      <AlertDemographics {...visit} />
      <AlertsList {...props} />
      <OverrideDialogue api={apis.alerts} />
      <EditDialog api={apis.edits} />
    </>
  );
}
